import AmpRealtime from '@amplify/amp-realtime';

import {
  ACTIVITY_ASSIGNED_EVENT,
  ASSIGNMENT_UPDATED_EVENT,
  PULL_TO_LESSON_EVENT,
} from 'src/constants/realtime.constants';
import {
  RealtimeCallback,
  RealtimePresentationCallback,
  RealtimePullToLessonCallback,
} from 'src/interfaces/api.types';
import { Class } from 'src/interfaces/class.types';
import { getAmpConfig } from 'src/services/amp-config.service';
import authService from 'src/services/api/auth.service';
import { generateRealtimeChannelNameEventKey } from 'src/utils/realtime.utils';

class Realtime {
  private _rt: AmpRealtime | null = null;
  private _channelNames: string[] = [];
  private _listeners: Record<string, RealtimeCallback> = {};
  private _hasInitialized = false;

  init(userClasses: Class[]) {
    if (this._hasInitialized) return;
    // for local env, config located in public folder will be used
    const config = getAmpConfig();
    const userBk = authService.getUserBk();
    const userRole = authService.getRole();

    // any issues with this lib may be because of new 5.0 version
    // you may contact pioneers in case of problems
    this._rt = new AmpRealtime(config, userBk);

    this._rt.classStateService.init(
      userClasses.map((cl) => cl.sourcedId),
      userRole,
    );

    this._hasInitialized = true;
  }

  get hasInitialized() {
    return this._hasInitialized;
  }

  listenToPresentationStateChange(callback: RealtimePresentationCallback) {
    this._rt?.classStateService.registerStateChangeCallback(callback);
  }

  removeListenerForPresentationStateChange(
    callback: RealtimePresentationCallback,
  ) {
    this._rt?.classStateService.unregisterStateChangeCallback(callback);
  }

  subscribeToAssignments(
    newAssignmentCallback: RealtimeCallback,
    updateAssignmentCallback: RealtimeCallback,
    classes?: Class[],
  ) {
    const userClasses = classes ?? [];
    const eventKey = generateRealtimeChannelNameEventKey(
      ACTIVITY_ASSIGNED_EVENT,
    );

    this._channelNames = userClasses.map(
      (item) => `${item.sourcedId}-${eventKey}`,
    );
    this._listeners = {
      [ACTIVITY_ASSIGNED_EVENT]: newAssignmentCallback,
      [ASSIGNMENT_UPDATED_EVENT]: updateAssignmentCallback,
    };

    this._rt?.subscribeToChannels(this._channelNames);

    Object.entries(this._listeners).forEach(([event, callback]) => {
      this._rt?.addEventListener(event, callback);
    });
  }

  unsubscribeFromAssignments() {
    Object.entries(this._listeners).forEach(([event, callback]) => {
      this._rt?.removeEventListener(event, callback);
    });

    this._rt?.unsubscribeFromChannels(this._channelNames);
  }

  listenToDesmosPullToLesson(callback: RealtimePullToLessonCallback) {
    this._rt?.addEventListener(PULL_TO_LESSON_EVENT, callback);
  }

  removeListenerForDesmosPullToLesson(callback: RealtimePullToLessonCallback) {
    this._rt?.removeEventListener(PULL_TO_LESSON_EVENT, callback);
  }
}

const realtimeService = new Realtime();

export default realtimeService;
